import { useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import { LargeView, SmallView } from 'common/layout';
import SearchBox, { Position } from 'common/searchBox';
import { DateFormat, IItinerary } from 'common/types';
import {
  getClickCodeWithPrefix,
  getClickCodeOfList,
  stringifyDepartureDate,
  stringifyDepartureMonths,
} from 'common/utils';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import {
  AP_CODES,
  SeatClass,
  TripType,
  getPassengerDesc,
  getSeatDesc,
  useSearchParamsContext,
} from 'contexts/searchParams';
import { continentDescMap } from 'everywhere/daily/container/common';
import useAirportDetailList, {
  getSummarizedLocationCodes,
} from 'hooks/useAirportDetailList';
import { useContext, useEffect, useState } from 'react';
import Airport from './Airport';
import Date from './Date';
import GET_COUNTRY from './country.gql';
import styles from './index.module.scss';
import PriceAlert from './price-alert';

const cx = classNames.bind(styles);

interface IProps {
  adult: number;
  child: number;
  infant: number;
  continentId?: number;
  countryCode?: string;
  departureMonths?: Set<string>;
  disablePriceAlert: boolean;
  expandMultiCity: boolean;
  fareType?: string;
  isDirect?: boolean;
  isDomestic?: boolean;
  isLoading?: boolean;
  itinerary: IItinerary[];
  position: Position;
  stayLength?: string;
  tripType: TripType;
}

export default function SearchCondition({
  adult,
  child,
  infant,
  continentId,
  countryCode,
  departureMonths,
  disablePriceAlert,
  expandMultiCity,
  fareType,
  isDirect = false,
  isDomestic = false,
  isLoading = false,
  itinerary,
  position,
  stayLength,
  tripType,
}: IProps) {
  const setModal = useContext(ModalFuncContext);
  const { setSearchParams } = useSearchParamsContext();
  const [focusOnSeat, setFocusOnSeat] = useState(false);
  const [showSearchBox, toggleSearchBox] = useState(false);
  const airportCodes = getSummarizedLocationCodes(itinerary);
  const { data: { airportDetailList } = { airportDetailList: [] } } =
    useAirportDetailList(airportCodes);
  const { data: { country } = { country: null } } = useQuery(GET_COUNTRY, {
    variables: { countryCode },
    skip: !(tripType === TripType.EVERYWHERE && countryCode),
  });
  const airportMap = new Map(
    airportDetailList.map(airport => [airport.iataCode, airport]),
  );

  const getAirport = (iataCode: string, reverse: boolean = false) => {
    if (iataCode !== AP_CODES.EVERYWHERE) {
      return {
        code: iataCode,
        name: airportMap.get(iataCode)?.cityName || '',
        isDomestic: airportMap.get(iataCode)?.isDomestic || false,
      };
    }
    if (country && countryCode) {
      return {
        code: countryCode,
        name: country.countryName,
        isDomestic: false,
      };
    }
    if (continentId && continentDescMap.get(continentId)) {
      const continentName = continentDescMap.get(continentId)!;
      return {
        ...(reverse
          ? { code: '모든 곳 검색', name: continentName }
          : { code: continentName, name: '모든 곳 검색' }),
        isDomestic: false,
      };
    }
    return {
      code: AP_CODES.EVERYWHERE,
      name: '모든 곳 검색',
      isDomestic: false,
    };
  };

  const initialize = (isMO = true) => {
    if (!airportDetailList.length) return;
    const initialSearchParmams = {
      seatClass: fareType as SeatClass,
      activeIndex: 0,
      itinerary: itinerary.map(
        ({ departureAirport, arrivalAirport, departureDate }, index) => ({
          departureCity: getAirport(departureAirport),
          arrivalCity: getAirport(arrivalAirport),
          departureDate:
            tripType === TripType.RT && stayLength && index === 1
              ? undefined
              : departureDate,
        }),
      ),
      ...(isMO && { tripType }),
      isDirect,
      passengers: { adult, child, infant },
      departureMonths,
      stayLength,
    };
    setSearchParams(prev => ({ ...prev, ...initialSearchParmams }));
  };

  useEffect(() => {
    initialize();
  }, [
    adult,
    child,
    infant,
    fareType,
    isDirect,
    isDomestic,
    itinerary,
    tripType,
    airportDetailList,
  ]);

  const airports = airportCodes.map(code => getAirport(code, true));
  const isDateSelected = itinerary.every(({ departureDate }) => departureDate);
  const departureDates =
    tripType === TripType.EVERYWHERE && !isDateSelected
      ? [stringifyDepartureMonths(departureMonths)]
      : itinerary.map(
          stringifyDepartureDate(tripType, stayLength, DateFormat.DotMDd),
        );
  const seatClass = fareType ? getSeatDesc(isDomestic, fareType) : '';

  const openSearchBox = () => {
    initialize();
    toggleSearchBox(true);
  };

  const closeSearchBox = () => {
    toggleSearchBox(false);
    setFocusOnSeat(false);
  };

  const onClickAirport = (index: number) => () => {
    openSearchBox();
    setModal({
      position: ModalPosition.LIST,
      type:
        tripType === TripType.RT || tripType === TripType.OW
          ? index % 2 === 0
            ? Modal.DEPARTURES
            : Modal.ARRIVALS
          : Modal.NULL,
    });
  };

  const onClickDate = (index: number) => () => {
    openSearchBox();
    setModal({
      position: ModalPosition.LIST,
      type: Modal.CALENDAR,
      payload: { index },
    });
  };

  const onClickPassSeat = () => {
    openSearchBox();
    setFocusOnSeat(true);
  };

  const withPrefix = getClickCodeWithPrefix(
    getClickCodeOfList(tripType, isDomestic),
  );

  const isPriceAlertsAvailable =
    !disablePriceAlert &&
    [TripType.RT, TripType.OW].includes(tripType) &&
    !stayLength;

  return (
    <>
      <LargeView>
        <SearchBox
          disablePriceAlert={!isPriceAlertsAvailable}
          expandMultiCity={expandMultiCity}
          isLarge
          isLoading={isLoading}
          position={position}
          searchParams={{
            adultCount: adult,
            childCount: child,
            infantCount: infant,
            cabinClass: fareType as any,
            isNonstop: isDirect,
            itineraries: itinerary.map(
              ({ departureAirport, arrivalAirport, departureDate }) => ({
                departureLocationCode: departureAirport,
                departureLocationName:
                  airportMap.get(departureAirport)?.cityName,
                arrivalLocationCode: arrivalAirport,
                arrivalLocationName: airportMap.get(arrivalAirport)?.cityName,
                departureDate,
              }),
            ),
            tripType: tripType as any,
          }}
          showSeat={focusOnSeat}
          initialize={initialize}
          onAfterSearch={closeSearchBox}
        />
      </LargeView>
      <SmallView>
        <div className={cx('SearchCondition')} aria-expanded={showSearchBox}>
          <div className={cx('current')}>
            <div className={cx('top')}>
              <strong className={cx('route')}>
                {airports.map(({ code, name }, index) => (
                  <Airport
                    key={`${code}_${index}`}
                    code={code}
                    name={name}
                    data-event-nlog="click"
                    data-event-area-code={withPrefix('city')}
                    onClick={onClickAirport(index)}
                  />
                ))}
              </strong>
              {isPriceAlertsAvailable && (
                <PriceAlert
                  isLoading={isLoading}
                  searchParams={{
                    adultCount: adult,
                    childCount: child,
                    infantCount: infant,
                    cabinClass: fareType as any,
                    isNonstop: isDirect,
                    itineraries: itinerary.map(
                      ({
                        departureAirport,
                        arrivalAirport,
                        departureDate,
                      }) => ({
                        departureLocationCode: departureAirport,
                        departureLocationName:
                          airportMap.get(departureAirport)?.cityName,
                        arrivalLocationCode: arrivalAirport,
                        arrivalLocationName:
                          airportMap.get(arrivalAirport)?.cityName,
                        departureDate,
                      }),
                    ),
                    tripType: tripType as any,
                  }}
                />
              )}
            </div>
            <div className={cx('desc')}>
              {departureDates.map((date, index) => (
                <Date
                  key={`${date}${index}`}
                  date={date}
                  isLast={index === departureDates.length - 1}
                  data-event-nlog="click"
                  data-event-area-code={withPrefix('date')}
                  onClick={onClickDate(index)}
                />
              ))}
              {tripType !== TripType.EVERYWHERE && (
                <em className={cx('seat')}>
                  <button
                    type="button"
                    data-event-nlog="click"
                    data-event-area-code={withPrefix('passeat')}
                    onClick={onClickPassSeat}
                  >
                    {getPassengerDesc({ adult, child, infant })}
                    {seatClass && `, ${seatClass}`}
                  </button>
                </em>
              )}
            </div>
            <button
              type="button"
              className={cx('change')}
              data-event-nlog="click"
              data-event-area-code={withPrefix('searchboxopen')}
              onClick={openSearchBox}
            >
              <span className={cx('text')}>변경</span>
            </button>
          </div>
          {showSearchBox && (
            <div className={cx('search')}>
              <SearchBox
                expandMultiCity={expandMultiCity}
                disablePriceAlert={!isPriceAlertsAvailable}
                isLoading={isLoading}
                position={position}
                searchParams={{
                  adultCount: adult,
                  childCount: child,
                  infantCount: infant,
                  cabinClass: fareType as any,
                  isNonstop: isDirect,
                  itineraries: itinerary.map(
                    ({ departureAirport, arrivalAirport, departureDate }) => ({
                      departureLocationCode: departureAirport,
                      arrivalLocationCode: arrivalAirport,
                      departureDate,
                    }),
                  ),
                  tripType: tripType as any,
                }}
                showSeat={focusOnSeat}
                initialize={initialize}
                onAfterSearch={closeSearchBox}
              />
              <button
                type="button"
                className={cx('toggle')}
                data-event-nlog="click"
                data-event-area-code={withPrefix('searchboxclose')}
                onClick={closeSearchBox}
              >
                닫기
              </button>
            </div>
          )}
        </div>
      </SmallView>
    </>
  );
}
